import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import i18n from 'i18n';
import { get } from 'lodash';
import apiCubic from 'config/api-cubic';
import { MODAL_TYPES } from 'constants/modal-types';
import { backToPageThatCausedAuth, resetAuthSteps, resetUserData } from 'actions/app';
import { showModal, hideModal } from 'actions/modal';
import { hideLoader, showLoader } from 'actions/loader';
import { getContractNumberSelector } from 'reducers/profile/selectors';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import useChangeMainPackage from 'hooks/data/useChangeMainPackage';
import useModals from 'hooks/useModals';
import useActivateAdditionalPackage from 'hooks/data/useActivateAdditionPackage';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import usePinProtectModal from '../pages/Profile/Packages/usePinProtectModal';
import { store } from '../index';
import { selectAuthSteps } from 'reducers/app/selectors';
import { showToast } from 'actions/toast';

const useChangePackage = (successLink = '', focusBackConfig = { sectionToFocus: '@' }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const showProtectedModal = usePinProtectModal();
  const { showPortmonePaymentModal } = useModals();
  const authSteps = useSelector(selectAuthSteps);

  const handleModalClose = useCallback(() => {
    dispatch(hideModal());
    SpatialNavigationWrapper.focus('@ACTION_BUTTONS');
  }, [dispatch]);

  const successHandler = useCallback(async () => {
    const searchParams = new URLSearchParams(history.location.search);
    handleModalClose();
    queryClient.resetQueries();
    dispatch(resetUserData());
    // SMRT-282 можно добавить -1 для канала, и authSteps если нет.
    if (successLink) {
      // Clear package change steps
      history.go(-2);
      if (authSteps > 0) {
        const stepsToBack = successLink.includes('channelId') ? authSteps - 1 : authSteps;
        history.go(-stepsToBack);
        dispatch(resetAuthSteps());
      }
      setTimeout(() => history.replace(successLink), 100);
    } else if (searchParams.has('pkgId')) {
      await dispatch(backToPageThatCausedAuth());
      // https://hootko.atlassian.net/browse/STVWL-572
      // To main page if we came from subscriptions
      setTimeout(() => history.replace('/'), 100);
    } else {
      history.goBack();
    }
  }, [dispatch, handleModalClose, history, queryClient, successLink, authSteps]);

  const { mutateAsync: changeMainPackage, isLoading: isMainPackageChanging } = useChangeMainPackage({
    onSuccess: (_, { pkg }) => {
      innerAnalyticsVolia.postEvent({
        eventGroup: 'purchase SVOD',
        eventName: 'main',
        eventValue: `package code="${pkg.id}"; success`,
      });

      successHandler();

      dispatch(showToast({ options: { toastText: i18n.t('common.toast.successPurchase') } }));
    },
    onError: ({ code, message }, { pkg }) => {
      // Not enough money
      // Extra modal fot DG users, normal users can't get here anymore, cause of QR Page

      if (code === 'orderTm_error_1') {
        dispatch(
          showModal(MODAL_TYPES.CONFIRM, {
            message: message,
            confirmText: i18n.t('common.repeat'),
            rejectText: i18n.t('common.close'),
            onModalConfirm: () => {
              dispatch(hideModal());
              dispatch(changePackage(pkg));
            },
            onModalBack: () => {
              // https://hootko.atlassian.net/browse/STVWL-576
              const searchParams = new URLSearchParams(history.location.search);
              if (!searchParams.has('pkgId')) history.go(-2);

              dispatch(backToPageThatCausedAuth());
              handleModalClose();
            },
          }),
        );
        return;
      }
      innerAnalyticsVolia.postEvent({
        eventGroup: 'purchase SVOD',
        eventName: 'main',
        eventValue: `package code="${pkg.id}"; ${code}`,
      });

      innerAnalyticsVolia.postEvent({
        eventGroup: 'notification',
        eventName: code,
        eventValue: `show notification`,
      });

      dispatch(
        showModal(MODAL_TYPES.INFO, {
          onModalBack: handleModalClose,
          message,
        }),
      );
    },
  });

  const { mutateAsync: changeAdditionalPackage, isLoading: isAdditionalPkgChanging } = useActivateAdditionalPackage({
    onSuccess: (_, { pkg }) => {
      innerAnalyticsVolia.postEvent({
        eventGroup: 'purchase SVOD',
        eventName: 'additional',
        eventValue: `package code="${pkg.id}"; success`,
      });

      successHandler();
      dispatch(showToast({ options: { toastText: i18n.t('common.toast.successPurchase') } }));
    },
    onError: (error, { pkg }) => {
      // Not enough money
      // Extra modal fot DG users, normal users can't get here anymore, cause of QR Page
      if (error.code === 'orderSubTm_error_1') {
        dispatch(
          showModal(MODAL_TYPES.CONFIRM, {
            message: error.message,
            confirmText: i18n.t('common.repeat'),
            rejectText: i18n.t('common.close'),
            onModalConfirm: () => {
              dispatch(hideModal());
              dispatch(changePackage(pkg));
            },
            onModalBack: () => {
              // https://hootko.atlassian.net/browse/STVWL-576
              const searchParams = new URLSearchParams(history.location.search);
              if (!searchParams.has('pkgId')) history.go(-2);

              dispatch(backToPageThatCausedAuth());
              handleModalClose();
            },
          }),
        );
        return;
      }
      innerAnalyticsVolia.postEvent({
        eventGroup: 'purchase SVOD',
        eventName: 'additional',
        eventValue: `package code="${pkg.id}"; ${error.code}`,
      });

      innerAnalyticsVolia.postEvent({
        eventGroup: 'notification',
        eventName: error.code,
        eventValue: `show notification`,
      });

      const message = error.message;

      dispatch(
        showModal(MODAL_TYPES.INFO, {
          onModalBack: handleModalClose,
          message,
        }),
      );
    },
  });

  const changePackage = useCallback(
    (pkg) => {
      const isAdditionalPkg = !!pkg.isAdditional;
      // Need to get the most actual data during this "changePackage" call
      const contractCode = getContractNumberSelector(store.getState());
      const isPurchaseProtected = get(store.getState(), ['parental', 'data', 'purchase']);

      innerAnalyticsVolia.postEvent({
        eventGroup: 'subscription management',
        eventName: `click order`,
        eventValue: `package code="${pkg.id}"`,
      });

      // TSU price is not available from cubic api right now
      // const onMainPackageChange = async () => {
      //   // TODO: remove TSU price
      //   const tsuPrice = get(pkg, 'bssMetadata.tsuPrice');

      //   if (tsuPrice) {
      //     innerAnalyticsVolia.postEvent({
      //       eventGroup: 'notification',
      //       eventName: `cost is less then current`,
      //       eventValue: `show notification`,
      //     });

      //     dispatch(
      //       showModal(MODAL_TYPES.NOTIFICATION, {
      //         onModalBack: () => {
      //           innerAnalyticsVolia.postEvent({
      //             eventGroup: 'notification',
      //             eventName: `cost is less then current`,
      //             eventValue: `click cancel`,
      //           });
      //           handleModalClose();
      //         },
      //         title: i18n.t('packages.changeMainPackageModal.title'),
      //         description: i18n.t('packages.changeMainPackageModal.message', { tsuPrice }),
      //         buttons: [
      //           {
      //             text: i18n.t('packages.changeMainPackageModal.submit'),
      //             handler: () => {
      //               handleModalClose();
      //               innerAnalyticsVolia.postEvent({
      //                 eventGroup: 'notification',
      //                 eventName: `cost is less then current`,
      //                 eventValue: `click accept`,
      //               });
      //               changeMainPackage({ pkg, contractCode });
      //             },
      //           },
      //           {
      //             text: i18n.t('packages.changeMainPackageModal.cancel'),
      //             handler: () => {
      //               innerAnalyticsVolia.postEvent({
      //                 eventGroup: 'notification',
      //                 eventName: `cost is less then current`,
      //                 eventValue: `click cancel`,
      //               });
      //               handleModalClose();
      //             },
      //           },
      //         ],
      //       }),
      //     );
      //   } else {
      //     await changeMainPackage({ pkg, contractCode });
      //   }
      // };

      const performBuy = async (...params) => {
        try {
          dispatch(showLoader());
          isAdditionalPkg ? await changeAdditionalPackage(...params) : await changeMainPackage(...params);
        } catch {
          // nothing
        } finally {
          dispatch(hideLoader());
        }
      };

      const action = async () => {
        let cost = null;
        try {
          dispatch(showLoader());
          // https://hootko.atlassian.net/browse/STVWL-525
          const { data: priceData } = await apiCubic.post({
            endpoint: isAdditionalPkg ? apiCubic.endpoints.getSumForAdditionalSubs : apiCubic.endpoints.getSumForSubs,
            data: {
              contractCode,
              [isAdditionalPkg ? 'subTmCode' : 'tmCode']: pkg.id,
            },
          });
          cost = priceData.payload.moneyToPay;
        } catch (error) {
          console.error(error);
        } finally {
          dispatch(hideLoader());
        }

        if (cost <= 0) {
          performBuy({ pkg, contractCode });
        } else {
          const { name: title } = pkg;
          const onSuccess = () => performBuy({ pkg, contractCode });
          const onCancel = () => {
            // it means that we came from channel or vod and need to clear pkg selection steps
            if (successLink) history.go(-2);
            dispatch(backToPageThatCausedAuth());
          };
          showPortmonePaymentModal({ title, cost, contractCode, onSuccess, onCancel }, focusBackConfig);
        }
      };

      isPurchaseProtected ? showProtectedModal(action, handleModalClose) : action();
    },
    [
      showProtectedModal,
      handleModalClose,
      dispatch,
      changeAdditionalPackage,
      changeMainPackage,
      showPortmonePaymentModal,
      focusBackConfig,
      successLink,
      history,
    ],
  );

  const isLoading = isAdditionalPkgChanging || isMainPackageChanging;

  return { changePackage, isLoading };
};

export default useChangePackage;
